'use client';
import React, { useCallback, useEffect, useState } from 'react';
import Breadcrumb from '@/components/molecules/breadcrumb';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import { sdk } from '@/sdk/CommercetoolsSDK';
import Typography from '@/components/atoms/typography';
import { useParams, usePathname } from 'next/navigation';
import Link from 'next/link';
import Image from '@/components/atoms/Image';
import RelatedProducts from './related-products';
import Spinner from '../spinner';


// Define supported locales and a helper to map route params safely
type SupportedLocale = 'en-AU' | 'en-NZ';
const toSupportedLocale = (s?: string): SupportedLocale => (s === 'nz' ? 'en-NZ' : 'en-AU');

type VideoItem = {
    idProduct: string;
    idItem: string;
    category: string;
    idVideo: string;
    videoName: string;
    videoUrl: string; // Vimeo ID
    videoType: string;
    videoThumbnail: string;
    description: string;
    videoDuration: string; // minutes as string
    dateCreated: string;
    dateUpdated: string;
    variantKey: string;
};

type VideoDetailsProps = {
    data: VideoItem[];
};

type Product = {
    SKU: string;
    Colour: string;
    ItemCode: string;
    Images: { url: string; label: string }[];
    Slug: Record<SupportedLocale, string>;
    Name: Record<SupportedLocale, string>;
    TradePrice: Record<SupportedLocale, string>;
    Range: string;
    Prices: {
        id: string;
        country: string;
        value: {
            type: string;
            currencyCode: string;
            centAmount: number;
            fractionDigits: number;
        };
    }[];
};

const VideoDetails = () => {
    const extensions = sdk.composableCommerce;
    const [componentsProducts, setComponentsProducts] = useState<Product[]>([]);
    const { locale } = useParams();
    const localeString = Array.isArray(locale) ? locale[0] : locale || '';
    const localeKey: SupportedLocale = toSupportedLocale(localeString);
    const pathname = usePathname();
    const id = pathname.split('/').filter(Boolean).pop(); // Extract the `id` from the URL
    const [data, setData] = useState<VideoDetailsProps['data']>([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState<VideoItem | undefined>(undefined);

    useEffect(() => {
        async function fetchVideoList() {
            const regex = /Calling extension(?: \S+)? failed/;
            const maxRetries = 3;
            const retryDelay = 1000; // 1 second delay between retries

            try {
                setLoading(true);

                for (let attempt = 0; attempt < maxRetries; attempt++) {
                    try {
                        const response = await extensions.custom.videoList({});
                        if (response?.data?.data && Array.isArray(response?.data?.data.videoList)) {
                            setData(response?.data?.data.videoList);
                        } else {
                            console.warn('API response data is not an array:', response?.data?.data);
                            setData([]);
                        }
                        break; // Success - exit retry loop
                    } catch (error: any) {
                        const errorMessage = error?.message || error?.toString() || '';

                        // Check if error matches the retry pattern
                        if (regex.test(errorMessage) && attempt < maxRetries - 1) {
                            console.warn(`Attempt ${attempt + 1} failed with extension error. Retrying...`);
                            // Wait before retrying
                            await new Promise(resolve => setTimeout(resolve, retryDelay * (attempt + 1)));
                            continue;
                        }

                        // If it's not a retryable error or we've exhausted retries, throw
                        throw error;
                    }
                }
            } catch (error) {
                console.error('Error fetching video list:', error);
            } finally {
                setLoading(false);
            }
        }
        fetchVideoList();
    }, [extensions]);


    useEffect(() => {
        // Update the selected video if `id` or `data` changes
        const video = data?.find((video) => video.idVideo === id);
        setSelected(video);
    }, [id, data]);

    const uploadedDate = selected?.dateCreated
        ? new Date(selected.dateCreated)
        : undefined;

    const [relatedVideosByType, setRelatedVideosByType] = useState<VideoItem[]>([]);

    useEffect(() => {
        if (!selected?.videoType) {
            setRelatedVideosByType([]);
            return;
        }
        const selectedType = selected.videoType.toString().trim().toLowerCase();

        // First filter by type and exclude the current video
        const filteredByType = (data || []).filter(
            (v) => v.idVideo !== selected.idVideo &&
                (v.videoType?.toString().trim().toLowerCase() ?? '') === selectedType
        );

        // Then remove duplicates based on idVideo
        const uniqueVideos = Array.from(new Map(
            filteredByType.map(video => [video.idVideo, video])
        ).values());

        setRelatedVideosByType(uniqueVideos);
    }, [selected, data]);

    const formatDDMMYYYY = (d?: Date) => {
        if (!d) return '';
        const dd = String(d.getDate()).padStart(2, '0');
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const yyyy = d.getFullYear();
        return `${dd}/${mm}/${yyyy}`;
    };

    const fetchBundleData = useCallback(
        async (productCodes: string[]) => {
            if (!productCodes || productCodes.length === 0) return;
            let storeKey: string | null = '';
            if (typeof window !== 'undefined') {
                storeKey = localStorage.getItem('store');
            }
            const requestData = {
                variant: productCodes,
                storeKey,
            };
            try {
                const responseBody = await extensions.product.bundleProducts(JSON.stringify(requestData));
                setComponentsProducts(responseBody?.data?.data);
            } catch (error) {
                console.error('Bundle Data Fetch Error:', error);
            }
        },
        [extensions.product],
    );

    useEffect(() => {
        if (!data || !id) return;

        // Find all variantKeys dynamically for the current idVideo
        const matchingVariantKeys = data
            .filter((video) => video.idVideo === id)
            .map((video) => video.variantKey);

        // Remove duplicates if any
        const uniqueVariantKeys = [...new Set(matchingVariantKeys)];

        // Update fetchBundleData to use all matching variant keys
        if (uniqueVariantKeys.length > 0) {
            fetchBundleData(uniqueVariantKeys);
        }

    }, [id, data, fetchBundleData]);

    return (
        <>
            {loading && <Spinner />}
            {loading && <div className="overlay fixed left-0 top-0 z-50 h-full w-full bg-gray-800 opacity-75"></div>}
            <div className=''>
                <div className="px-4 lg:px-10">
                    <Breadcrumb Separator={<ChevronRightIcon className="w-4" />} >
                        <Link href={'/'}>
                            <span className="text-12 !font-normal capitalize leading-6 text-black">
                                Home
                            </span>
                        </Link>
                        <Link href={'/video-list'}>
                            <span className="text-12 !font-normal capitalize leading-6 text-black">
                                Video Library
                            </span>
                        </Link>
                    </Breadcrumb>
                </div>
                {selected && (
                    <>
                        <div className='mt-10 px-4'>
                            <div className=" bg-white shadow-[0_0_20px_0_rgba(0,0,0,0.1)] lg:mx-32">
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                    {/* Video */}
                                    <div className="w-full bg-black">
                                        <div className="relative w-full aspect-video lg:aspect-auto">
                                            <iframe
                                                className="w-full h-[300px] sm:h-[400px] lg:h-[660px]"
                                                src={`https://player.vimeo.com/video/${selected?.videoUrl}`}
                                                title={selected?.videoName}
                                                allow="autoplay; fullscreen; picture-in-picture"
                                                allowFullScreen
                                            />
                                        </div>
                                    </div>
                                    {/* Text Content */}
                                    <div className="flex flex-col p-4">
                                        <h2 className="text-22 font-semibold text-gray-900">{selected?.videoName}</h2>
                                        <p className="text-16 font-medium text-gray-600 mt-2">Uploaded : {formatDDMMYYYY(uploadedDate)}</p>
                                        {selected?.videoType && (
                                            <div className="inline-block mr-auto mt-4 text-14 font-medium py-1 px-2 text-gray-700 bg-[#ececec] rounded">
                                                {selected?.videoType}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="bg-[#F3EDE9]  mt-10 py-10 lg:px-16 lg:py-10">
                            {/* <RelatedProducts
                                products={componentsProducts}
                                localeKey={localeKey}
                                selectedCategory={selected?.category}
                            /> */}

                            <div className="">
                                <div className="lg:px-10 px-4">
                                    {/* <hr className='mt-5 border-[#747474]' /> */}
                                    <Typography className=' font-medium text-28 leading-8 lg:leading-[63px] lg:text-48'>Similar Videos</Typography>

                                    {relatedVideosByType && relatedVideosByType.length > 0 && (
                                        <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6">
                                            {relatedVideosByType.slice(0, 8).map((video) => (
                                                <Link
                                                    href={`/video-list/${video.idVideo}`}
                                                    key={`${video.idVideo}-${video.idItem}`}
                                                    className="block group relative bg-white rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-shadow duration-300"
                                                >
                                                    <div className="relative aspect-video bg-gray-100">
                                                        <Image
                                                            priority
                                                            src={video.videoThumbnail}
                                                            alt={video.videoName ?? ''}
                                                            style={{ objectPosition: 'center' }}
                                                            className="w-full h-full !relative rounded-sm group-hover:opacity-75"
                                                            fill
                                                            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                                                        />
                                                        <div className="absolute top-2 lg:top-4 right-2 bg-[#48C9B080] bg-opacity-70 text-white text-xs px-[18px] py-[10px] rounded flex items-center">
                                                            <span className="text-16 font-bold">
                                                                {video.videoDuration.trim()} {video.videoDuration.trim() === '1' ? 'min' : 'mins'}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <div className="p-4">

                                                        <h3 className="font-medium text-gray-900 mb-2 line-clamp-2">
                                                            {video.videoName}
                                                        </h3>
                                                        <div className="flex justify-between items-start">
                                                            <span className="text-12 font-medium text-gray-700 bg-[#ececec] px-2 py-1 rounded">
                                                                {video.videoType}
                                                            </span>

                                                        </div>

                                                    </div>
                                                </Link>
                                            ))}
                                        </div>
                                    )}
                                </div>

                            </div>
                        </div>
                    </>
                )}
            </div>
        </>
    );
};

export default VideoDetails;
